"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const bentoLayouts = ["md:col-span-7 md:row-span-2 aspect-[16/10] md:aspect-auto", "md:col-span-5 aspect-[4/5]", "md:col-span-5 aspect-[4/3]", "md:col-span-4 aspect-[3/4]", "md:col-span-4 aspect-[4/3]", "md:col-span-4 aspect-[4/3]", "md:col-span-6 aspect-[16/10]", "md:col-span-6 aspect-[16/10]", "md:col-span-3 aspect-[3/4]", "md:col-span-5 aspect-[4/3]", "md:col-span-4 aspect-[4/3]", "md:col-span-12 aspect-[21/9]"];
const fallbackHauls = [{
  route: "THE PROTAGONIST",
  miles: "SCENE 01",
  items: "HEADSHOT",
  date: "NYC 2024"
}, {
  route: "HAMLET'S GHOST",
  miles: "SCENE 04",
  items: "THEATER",
  date: "CHI 2024"
}, {
  route: "NOIR SHADOWS",
  miles: "SCENE 12",
  items: "FILM",
  date: "LA 2024"
}, {
  route: "THE ANTAGONIST",
  miles: "SCENE 08",
  items: "STILL",
  date: "ATL 2024"
}, {
  route: "SILENT DIALOGUE",
  miles: "SCENE 02",
  items: "HEADSHOT",
  date: "NYC 2023"
}, {
  route: "MACBETH REHEARSAL",
  miles: "SCENE 09",
  items: "THEATER",
  date: "LON 2023"
}, {
  route: "URBAN SOLITUDE",
  miles: "SCENE 15",
  items: "FILM",
  date: "PAR 2023"
}, {
  route: "THE LOVER",
  miles: "SCENE 03",
  items: "STILL",
  date: "NYC 2023"
}, {
  route: "BROKEN MIRROR",
  miles: "SCENE 21",
  items: "HEADSHOT",
  date: "LA 2023"
}, {
  route: "KING LEAR",
  miles: "SCENE 07",
  items: "THEATER",
  date: "CHI 2023"
}, {
  route: "MIDNIGHT CALL",
  miles: "SCENE 11",
  items: "FILM",
  date: "NYC 2022"
}, {
  route: "THE FINAL BOW",
  miles: "SCENE 99",
  items: "STILL",
  date: "GLOBAL"
}];
const fallbackImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_headshot_main.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_headshot_main.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_headshot_main.webp-wvc-sizes",
  alt: "High-contrast black and white headshot of Jordan Rivers"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_stage.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_stage.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_stage.png-wvc-sizes",
  alt: "Jordan Rivers performing on stage in dramatic lighting"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_film_noir_still.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_film_noir_still.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_film_noir_still.png-wvc-sizes",
  alt: "Cinematic film still featuring Jordan Rivers in shadow"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_character_study.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_character_study.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_character_study.webp-wvc-sizes",
  alt: "Close up character study of Jordan Rivers"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_theatrical_pose.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_theatrical_pose.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_theatrical_pose.webp-wvc-sizes",
  alt: "Jordan Rivers in a theatrical pose with soft silver lighting"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_backstage_moment.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_backstage_moment.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_backstage_moment.png-wvc-sizes",
  alt: "Candid backstage shot of Jordan Rivers"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_action_sequence.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_action_sequence.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_action_sequence.webp-wvc-sizes",
  alt: "Action sequence still from a recent film production"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_profile_eyes.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_profile_eyes.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_profile_eyes.webp-wvc-sizes",
  alt: "Sharp focus on eyes in a profile headshot"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_costume_detail.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_costume_detail.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_costume_detail.png-wvc-sizes",
  alt: "Detail of period costume worn by Jordan Rivers"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_rehearsal_room.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_rehearsal_room.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_rehearsal_room.png-wvc-sizes",
  alt: "Jordan Rivers in a rehearsal room setting"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_moody_exterior.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_moody_exterior.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_moody_exterior.png-wvc-sizes",
  alt: "Jordan Rivers on an exterior film set at night"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_showreel_cover.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_showreel_cover.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_showreel_cover.png-wvc-sizes",
  alt: "Wide cinematic shot from Jordan Rivers showreel"
}];
function GalleryGrid({
  view
}: {
  view: string;
}) {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
        {bentoLayouts.map((layout, i) => <div key={i} data-index={i} className={`${layout} bg-card/20 border border-border animate-pulse rounded-md`} />)}
      </div>;
  }
  if (error) {
    return <div className="border border-border p-12 text-center bg-card">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
          ARCHIVE_ERROR // SIGNAL_LOST
        </p>
        <Button variant="outline" onClick={refetch} className="rounded-md border-foreground/30 bg-transparent text-foreground font-mono text-xs uppercase tracking-[0.16em] hover:border-primary hover:text-primary transition-all">
          Reconnect
        </Button>
      </div>;
  }
  let items = bentoLayouts.map((layout, i) => {
    const post = posts[i];
    const fallback = fallbackHauls[i % fallbackHauls.length];
    const fallbackImg = fallbackImages[i % fallbackImages.length];
    const imageSrc = post?.featuredImage || fallbackImg.src;
    const useFeatured = Boolean(post?.featuredImage);
    const title = post?.title || fallback.route;
    const link = post?.link || "/VisualArchivePage";
    return {
      layout,
      i,
      post,
      fallback,
      fallbackImg,
      imageSrc,
      useFeatured,
      title,
      link
    };
  });
  if (view === "stills") {
    items = items.filter(item => item.fallback.items === "STILL");
  } else if (view === "reels") {
    items = items.filter(item => ["HEADSHOT", "THEATER", "FILM"].includes(item.fallback.items));
  }
  if (view === "index") {
    return <div className="flex flex-col border-t border-border">
        {items.map(({
        i,
        fallback,
        title,
        link
      }, idx) => <Link key={i} data-index={idx} to={link} className="group flex items-center justify-between py-6 border-b border-border hover:bg-card/50 transition-colors px-4">
            <div className="flex items-center gap-8">
              <span className="font-mono text-[0.65rem] text-primary opacity-40 group-hover:opacity-100 transition-opacity">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <h3 className="font-serif text-xl md:text-2xl text-foreground group-hover:text-primary transition-colors">
                {title}
              </h3>
            </div>
            <div className="flex items-center gap-12">
              <span className="font-mono text-[0.7rem] uppercase tracking-widest text-muted-foreground hidden md:block">
                {fallback.items}
              </span>
              <span className="font-mono text-[0.7rem] uppercase tracking-widest text-muted-foreground hidden sm:block">
                {fallback.date}
              </span>
              <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </Link>)}
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
        {items.map(({
        layout,
        i,
        post,
        fallback,
        fallbackImg,
        imageSrc,
        useFeatured,
        title,
        link
      }, idx) => <Link key={i} data-index={idx} to={link} className={`${layout} group relative overflow-hidden border border-border bg-card block rounded-md`}>
            <div className="absolute inset-0 overflow-hidden">
              {useFeatured ? <img src={imageSrc} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.05] grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100" /> : <img src={fallbackImg.src} data-wvc-srcset={fallbackImg.srcset} data-wvc-sizes={fallbackImg.sizes} alt={fallbackImg.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.05] grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100" />}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
            </div>

            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 left-0 w-3 h-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 top-0 h-3 w-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
            </span>
            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 right-0 w-3 h-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 top-0 h-3 w-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
            </span>
            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 left-0 w-3 h-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 bottom-0 h-3 w-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
            </span>
            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 right-0 w-3 h-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 bottom-0 h-3 w-px bg-primary opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
            </span>

            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
              <span className="font-mono text-[0.65rem] md:text-xs uppercase tracking-[0.2em] text-primary">
                FRM_{String(idx + 1).padStart(2, "0")}
              </span>
              <ArrowUpRight className="w-4 h-4 text-foreground/40 group-hover:text-primary transition-colors duration-300" strokeWidth={1.25} />
            </div>

            {post && <div className="absolute bottom-12 left-4 right-4 z-10">
                <h3 className="font-default text-base md:text-lg font-normal text-foreground line-clamp-2 leading-tight tracking-tight">
                  {post.title}
                </h3>
              </div>}

            <div className="absolute bottom-0 left-0 right-0 border-t border-border bg-background/90 group-hover:bg-primary transition-colors duration-500 z-10">
              <div className="flex items-center justify-between px-3 md:px-4 py-2 gap-2">
                <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.14em] text-foreground group-hover:text-primary-foreground truncate transition-colors duration-500">
                  {fallback.route}
                </span>
                <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground group-hover:text-primary-foreground/80 hidden sm:inline transition-colors duration-500">
                  {fallback.miles}
                </span>
                <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground group-hover:text-primary-foreground/80 hidden md:inline transition-colors duration-500">
                  {fallback.date}
                </span>
              </div>
            </div>

            <span aria-hidden="true" className="absolute bottom-9 left-0 h-px bg-primary w-0 group-hover:w-full transition-all duration-500 ease-out z-10" />
          </Link>)}
      </div>

      <div className="mt-16 md:mt-20 flex flex-col items-center gap-3">
        {hasNext ? <Button variant="ghost" onClick={async () => await loadMore()} disabled={isRefetching} className="rounded-md bg-transparent hover:bg-transparent text-foreground hover:text-primary font-mono text-xs uppercase tracking-[0.2em] px-0 group disabled:opacity-50">
            {isRefetching ? "DEVELOPING..." : "LOAD_MORE_FRAMES"}
            <ArrowUpRight className="w-3.5 h-3.5 ml-1 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.25} />
          </Button> : <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            END_OF_REEL
          </span>}
        <span aria-hidden="true" className="relative w-20 h-px bg-border">
          <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
          <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
        </span>
      </div>
    </>;
}
export default function TheHeadshotSeries() {
  const [view, setView] = useState("grid");
  const wp_query_params = {
    post_type: "post",
    per_page: 12,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1668" id="the-headshot-series" className="relative bg-background text-foreground py-24 md:py-32 px-6 md:px-22 overflow-hidden">
      <span aria-hidden="true" className="pointer-events-none select-none absolute left-4 md:left-16 top-32 md:top-40 font-serif font-light text-foreground/[0.02] leading-none" style={{
      fontSize: "28vw"
    }}>
        02
      </span>

      <div className="relative max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 mb-16 md:mb-24 pb-10 border-b border-border">
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 mb-6">
              <span aria-hidden="true" className="relative w-12 h-px bg-primary">
                <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
              </span>
            </div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
              ISO 800 / 24FPS / 4K
            </p>
          </div>

          <div className="md:col-span-6">
            <h2 className="font-default font-light text-foreground leading-[0.98] tracking-tight" style={{
            fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
          }}>
              <span className="font-serif">The Headshot </span>
              <span className="font-serif italic text-primary">Series</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground font-sans font-light leading-relaxed max-w-xl">
              A cinematic visual gallery featuring high-end headshots, dramatic production stills, and captured moments from the stage and screen.
            </p>
          </div>

          <div className="md:col-span-3 md:flex md:justify-end md:items-end">
            <div className="w-full overflow-x-auto md:overflow-visible">
              <ToggleGroup type="single" value={view} onValueChange={v => v && setView(v)} className="rounded-md inline-flex">
                {[{
                value: "grid",
                label: "Grid"
              }, {
                value: "stills",
                label: "Stills"
              }, {
                value: "reels",
                label: "Reels"
              }, {
                value: "index",
                label: "Index"
              }].map((opt, idx) => <ToggleGroupItem key={opt.value} data-index={idx} value={opt.value} aria-label={opt.label} className="rounded-md border border-border bg-transparent px-3 py-2 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-muted-foreground data-[state=on]:border-primary data-[state=on]:text-primary data-[state=on]:bg-transparent hover:text-foreground transition-colors">
                    {opt.label}
                  </ToggleGroupItem>)}
              </ToggleGroup>
            </div>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 12,
        order: "desc",
        orderby: "date"
      }}>
          <GalleryGrid view={view} />
        </PostsProvider>

        <div className="mt-20 md:mt-28 pt-8 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-muted-foreground">
            JORDAN RIVERS — PERFORMANCE ARCHIVE — EST. 2018
          </p>
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
            <Plus className="w-3 h-3 text-primary" strokeWidth={1.25} />
            TALENT_CODE / JR-PROTAGONIST
          </p>
        </div>
      </div>
    </section>;
}